import { IAPIRoutes } from "../../../../config";
import type {
  INeoProjectRepository,
  NeoAiCareSnfSettings,
  NeoAgentListResponse,
  NeoProject,
  NeoProjectActivityItem,
  NeoProjectActivityListResponse,
  NeoProjectFile,
  NeoProjectFileListResponse,
  NeoProjectFilePatchPayload,
  NeoProjectFilePayload,
  NeoProjectFileRoleRequest,
  NeoProjectDeleteResult,
  NeoProjectInstruction,
  NeoProjectInstructionListResponse,
  NeoProjectInstructionPayload,
  NeoProjectListResponse,
  NeoProjectMemoryListResponse,
  NeoProjectPayload,
  NeoProjectUpdatePayload,
  NeoProjectOwnersState,
  NeoPromptSuggestionListResponse,
  NeoShareState,
  NeoTemplateClonePayload,
  NeoTemplateListResponse,
  NeoTask,
  NeoTaskListResponse,
  NeoTaskPayload,
  NeoWorkflowDocumentListOptions,
  NeoWorkflowDocumentListResponse,
} from "../../../core/ports/driven/neoProjectRepository";

const env = import.meta.env;

const getApiBaseUrl = (): string =>
  env.VITE_API_GATEWAY_URL_V2 || env.VITE_API_GATEWAY_URL || "";

const fillRoute = (
  template: string,
  params: Record<string, string | number>
): string => {
  let out = template;
  for (const [key, value] of Object.entries(params)) {
    out = out.replace(`{${key}}`, String(value));
  }
  return out;
};

export type INeoProjectRepositoryFactory = ReturnType<
  typeof NeoProjectRepositoryFactory
>;

export const NeoProjectRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): INeoProjectRepository => {
  const { GET, POST, PATCH, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const authHeader = async () => ({
    Authorization: `Bearer ${await getAuthenticationToken()}`,
  });

  // ── Projects ──────────────────────────────────────────────────────────
  const listProjectsAPI = async (): Promise<NeoProjectListResponse> =>
    GET(APIRoutes.apiNeoProjects, { limit: 100 }, await authHeader());

  const createProjectAPI = async (
    payload: NeoProjectPayload
  ): Promise<NeoProject> =>
    POST(APIRoutes.apiNeoProjects, payload, await authHeader());

  // ── Templates ─────────────────────────────────────────────────────────
  const listTemplatesAPI = async (): Promise<NeoTemplateListResponse> =>
    GET(APIRoutes.apiNeoTemplates, {}, await authHeader());

  const listPromptSuggestionsAPI =
    async (): Promise<NeoPromptSuggestionListResponse> =>
      GET(APIRoutes.apiNeoPromptSuggestions, {}, await authHeader());

  const cloneTemplateAPI = async (
    templateId: number,
    payload: NeoTemplateClonePayload
  ): Promise<NeoProject> =>
    POST(
      fillRoute(APIRoutes.apiNeoTemplateClone, { template_id: templateId }),
      payload,
      await authHeader()
    );

  const updateProjectAPI = async (
    projectId: number,
    payload: NeoProjectUpdatePayload
  ): Promise<NeoProject> =>
    PATCH(
      fillRoute(APIRoutes.apiNeoProject, { project_id: projectId }),
      payload,
      await authHeader()
    );

  const deleteProjectAPI = async (
    projectId: number
  ): Promise<NeoProjectDeleteResult> =>
    DELETE(
      fillRoute(APIRoutes.apiNeoProject, { project_id: projectId }),
      {},
      await authHeader()
    );

  const getProjectShareAPI = async (
    projectId: number
  ): Promise<NeoShareState> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectShare, { project_id: projectId }),
      {},
      await authHeader()
    );

  const setProjectShareClinicsAPI = async (
    projectId: number,
    clinicIds: number[]
  ): Promise<NeoShareState> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectShare, { project_id: projectId }),
      { clinic_ids: clinicIds },
      await authHeader()
    );

  const getAiCareSnfSettingsAPI = async (
    clinicId: number
  ): Promise<NeoAiCareSnfSettings> =>
    GET(
      APIRoutes.apiNeoAiCareSnfSettings,
      { clinic_id: clinicId },
      await authHeader()
    );

  const setAiCareSnfEnabledAPI = async (
    clinicId: number,
    enabled: boolean
  ): Promise<NeoAiCareSnfSettings> =>
    PATCH(
      APIRoutes.apiNeoAiCareSnfSettings,
      { clinic_id: clinicId, enabled },
      await authHeader()
    );

  // ── Owners (opaque email strings) ──────────────────────────────────────
  const getProjectOwnersAPI = async (
    projectId: number
  ): Promise<NeoProjectOwnersState> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectOwners, { project_id: projectId }),
      {},
      await authHeader()
    );

  const setProjectOwnersAPI = async (
    projectId: number,
    emails: string[],
    ownerTiers: Record<string, "primary" | "secondary"> = {}
  ): Promise<NeoProjectOwnersState> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectOwners, { project_id: projectId }),
      { emails, owner_tiers: ownerTiers },
      await authHeader()
    );

  // ── Activity ──────────────────────────────────────────────────────────
  const listProjectActivityAPI = async (
    projectId: number
  ): Promise<NeoProjectActivityListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectActivity, { project_id: projectId }),
      { limit: 100 },
      await authHeader()
    );

  // ── Agents ────────────────────────────────────────────────────────────
  const listProjectAgentsAPI = async (
    projectId: number
  ): Promise<NeoAgentListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectAgents, { project_id: projectId }),
      { limit: 100 },
      await authHeader()
    );

  // ── Files ─────────────────────────────────────────────────────────────
  const listProjectFilesAPI = async (
    projectId: number
  ): Promise<NeoProjectFileListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectFiles, { project_id: projectId }),
      { limit: 100 },
      await authHeader()
    );

  const listProjectWorkflowDocumentsAPI = async (
    projectId: number,
    options?: NeoWorkflowDocumentListOptions
  ): Promise<NeoWorkflowDocumentListResponse> => {
    const params: Record<string, string | number> = {
      limit: options?.limit ?? 25,
      offset: options?.offset ?? 0,
    };
    if (options?.category) params.category = options.category;
    return GET(
      fillRoute(APIRoutes.apiNeoProjectDocuments, { project_id: projectId }),
      params,
      await authHeader()
    );
  };

  const listAgentFilesAPI = async (
    projectId: number,
    agentId: number
  ): Promise<NeoProjectFileListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectAgentFiles, {
        project_id: projectId,
        agent_id: agentId,
      }),
      { limit: 100 },
      await authHeader()
    );

  const addProjectFileAPI = async (
    projectId: number,
    payload: NeoProjectFilePayload
  ): Promise<NeoProjectFile> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectFiles, { project_id: projectId }),
      payload,
      await authHeader()
    );

  // Multipart upload — APIProvider does not model FormData bodies, so this
  // adapter owns the raw fetch (still the adapter layer, so no transport
  // detail leaks into the service/page).
  const addAgentFileAPI = async (
    projectId: number,
    agentId: number,
    file: File,
    description?: string,
    role?: NeoProjectFileRoleRequest
  ): Promise<NeoProjectFile> => {
    const token = (await getAuthenticationToken()) || "";
    const formData = new FormData();
    formData.append("file", file);
    if (description) formData.append("description", description);
    if (role) formData.append("role", role);
    const response = await fetch(
      getApiBaseUrl() +
        fillRoute(APIRoutes.apiNeoProjectAgentFiles, {
          project_id: projectId,
          agent_id: agentId,
        }),
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const json = (await response.json()) as { detail?: string };
        if (typeof json.detail === "string") detail = json.detail;
      } catch {
        /* keep generic */
      }
      throw new Error(detail);
    }
    return (await response.json()) as NeoProjectFile;
  };

  const patchAgentFileAPI = async (
    projectId: number,
    agentId: number,
    fileId: number,
    payload: NeoProjectFilePatchPayload
  ): Promise<NeoProjectFile> =>
    PATCH(
      fillRoute(APIRoutes.apiNeoProjectAgentFile, {
        project_id: projectId,
        agent_id: agentId,
        file_id: fileId,
      }),
      payload,
      await authHeader()
    );

  const deleteProjectFileAPI = async (
    projectId: number,
    fileId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectFile, {
        project_id: projectId,
        file_id: fileId,
      }),
      {},
      await authHeader()
    );
  };

  const deleteAgentFileAPI = async (
    projectId: number,
    agentId: number,
    fileId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectAgentFile, {
        project_id: projectId,
        agent_id: agentId,
        file_id: fileId,
      }),
      {},
      await authHeader()
    );
  };

  // ── Instructions ──────────────────────────────────────────────────────
  const listProjectInstructionsAPI = async (
    projectId: number
  ): Promise<NeoProjectInstructionListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectInstructions, { project_id: projectId }),
      { limit: 100 },
      await authHeader()
    );

  const listAgentInstructionsAPI = async (
    projectId: number,
    agentId: number
  ): Promise<NeoProjectInstructionListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectAgentInstructions, {
        project_id: projectId,
        agent_id: agentId,
      }),
      { limit: 100 },
      await authHeader()
    );

  const createAgentInstructionAPI = async (
    projectId: number,
    agentId: number,
    payload: NeoProjectInstructionPayload
  ): Promise<NeoProjectInstruction> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectAgentInstructions, {
        project_id: projectId,
        agent_id: agentId,
      }),
      payload,
      await authHeader()
    );

  const updateAgentInstructionAPI = async (
    projectId: number,
    agentId: number,
    instructionId: number,
    payload: NeoProjectInstructionPayload
  ): Promise<NeoProjectInstruction> =>
    PATCH(
      fillRoute(APIRoutes.apiNeoProjectAgentInstruction, {
        project_id: projectId,
        agent_id: agentId,
        instruction_id: instructionId,
      }),
      payload,
      await authHeader()
    );

  const deleteAgentInstructionAPI = async (
    projectId: number,
    agentId: number,
    instructionId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectAgentInstruction, {
        project_id: projectId,
        agent_id: agentId,
        instruction_id: instructionId,
      }),
      {},
      await authHeader()
    );
  };

  // ── Memories ──────────────────────────────────────────────────────────
  const listProjectMemoriesAPI = async (
    projectId: number
  ): Promise<NeoProjectMemoryListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectMemories, { project_id: projectId }),
      { limit: 100 },
      await authHeader()
    );

  const listAgentMemoriesAPI = async (
    projectId: number,
    agentId: number
  ): Promise<NeoProjectMemoryListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectAgentMemories, {
        project_id: projectId,
        agent_id: agentId,
      }),
      { limit: 100 },
      await authHeader()
    );

  const deleteAgentMemoryAPI = async (
    projectId: number,
    agentId: number,
    memoryId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectAgentMemory, {
        project_id: projectId,
        agent_id: agentId,
        memory_id: memoryId,
      }),
      {},
      await authHeader()
    );
  };

  // ── Tasks ─────────────────────────────────────────────────────────────
  const listProjectTasksAPI = async (
    projectId: number
  ): Promise<NeoTaskListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectTasks, { project_id: projectId }),
      { limit: 100 },
      await authHeader()
    );

  const createProjectTaskAPI = async (
    projectId: number,
    payload: NeoTaskPayload
  ): Promise<NeoTask> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectTasks, { project_id: projectId }),
      payload,
      await authHeader()
    );

  const updateProjectTaskAPI = async (
    projectId: number,
    taskId: number,
    payload: NeoTaskPayload
  ): Promise<NeoTask> =>
    PATCH(
      fillRoute(APIRoutes.apiNeoProjectTask, {
        project_id: projectId,
        task_id: taskId,
      }),
      payload,
      await authHeader()
    );

  const deleteProjectTaskAPI = async (
    projectId: number,
    taskId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectTask, {
        project_id: projectId,
        task_id: taskId,
      }),
      {},
      await authHeader()
    );
  };

  const runProjectTaskAPI = async (
    projectId: number,
    taskId: number
  ): Promise<NeoProjectActivityItem> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectTaskRun, {
        project_id: projectId,
        task_id: taskId,
      }),
      {},
      await authHeader()
    );

  return {
    listProjectsAPI,
    createProjectAPI,
    listTemplatesAPI,
    listPromptSuggestionsAPI,
    cloneTemplateAPI,
    updateProjectAPI,
    deleteProjectAPI,
    getProjectShareAPI,
    setProjectShareClinicsAPI,
    getAiCareSnfSettingsAPI,
    setAiCareSnfEnabledAPI,
    getProjectOwnersAPI,
    setProjectOwnersAPI,
    listProjectActivityAPI,
    listProjectAgentsAPI,
    listProjectFilesAPI,
    listProjectWorkflowDocumentsAPI,
    listAgentFilesAPI,
    addProjectFileAPI,
    addAgentFileAPI,
    patchAgentFileAPI,
    deleteProjectFileAPI,
    deleteAgentFileAPI,
    listProjectInstructionsAPI,
    listAgentInstructionsAPI,
    createAgentInstructionAPI,
    updateAgentInstructionAPI,
    deleteAgentInstructionAPI,
    listProjectMemoriesAPI,
    listAgentMemoriesAPI,
    deleteAgentMemoryAPI,
    listProjectTasksAPI,
    createProjectTaskAPI,
    updateProjectTaskAPI,
    deleteProjectTaskAPI,
    runProjectTaskAPI,
  };
};
